/**
 * Consultas e mutações do catálogo (`/products`), com TanStack Query.
 *
 * Duas observações importantes sobre a API real (verificadas contra o servidor
 * rodando, não só contra docs/API.md):
 * - `GET /products` e `GET /products/:id` trazem `accessories` como `string[]`
 *   simples (uma linha por acessório) — não como `Accessory[]` com id, que é o
 *   formato só da subcoleção `GET /products/:id/accessories`. Os tipos locais
 *   abaixo corrigem isso sem tocar em `lib/types.ts`.
 * - Os dois endpoints também trazem `nextPrice` (próxima versão futura, ou
 *   `null`), que não está em `lib/types.ts` — usado para o aviso discreto de
 *   reajuste agendado.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post, put } from '@/lib/api';
import type {
  Accessory,
  CreateProductInput,
  CreateProductPriceInput,
  PriceVersion,
  Product,
  ProductDetail,
  ProductsQuery,
  UpdatePriceVersionInput,
  UpdateProductInput,
} from '@/lib/types';

// -------------------------------------------------------------------- tipos --

/**
 * Nomes locais em português para os tipos do contrato — o resumo de catálogo e a
 * ficha completa do produto (com o histórico de versões de preço).
 */
export type ProdutoCatalogo = Product;
export type ProdutoCatalogoDetalhe = ProductDetail;

// ------------------------------------------------------------------ helpers --

/**
 * Único ponto de conversão IPI ↔ interface: a API guarda fração decimal
 * (0.0325), a interface sempre mostra e aceita percentual (3,25).
 */
export function fracaoParaPercentTexto(valor: number): string {
  return (valor * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Converte texto digitado em percentual ("3,25" ou "3.25") para fração decimal (0.0325). */
export function percentTextoParaFracao(texto: string): number {
  const normalizado = texto.trim().replace(/\./g, '').replace(',', '.');
  const n = Number(normalizado);
  return Number.isFinite(n) ? n / 100 : 0;
}

/** Converte texto digitado em reais ("24.829,20" ou "24829,20") para número. */
export function parseValorMonetario(texto: string): number {
  const normalizado = texto.trim().replace(/\./g, '').replace(',', '.');
  const n = Number(normalizado);
  return Number.isFinite(n) ? n : 0;
}

/** Data de hoje em `YYYY-MM-DD`, no fuso de São Paulo — mesma regra do servidor. */
export function hojeISO(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

function queryString(params: Record<string, string | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') usp.set(key, value);
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

const catalogoKeys = {
  lista: (filtro: ProductsQuery) => ['catalogo', 'lista', filtro] as const,
  produto: (id: number | undefined) => ['catalogo', 'produto', id] as const,
  todos: ['catalogo'] as const,
};

// ------------------------------------------------------------------ queries --

export function useProdutos(filtro: ProductsQuery = {}) {
  return useQuery({
    queryKey: catalogoKeys.lista(filtro),
    queryFn: () =>
      get<{ data: ProdutoCatalogo[] }>(
        `/products${queryString({ q: filtro.q, active: filtro.active, at: filtro.at })}`
      ).then((r) => r.data),
  });
}

export function useProduto(id: number | undefined) {
  return useQuery({
    queryKey: catalogoKeys.produto(id),
    queryFn: () => get<{ data: ProdutoCatalogoDetalhe }>(`/products/${id}`).then((r) => r.data),
    enabled: id !== undefined,
  });
}

// ---------------------------------------------------------------- mutações --

export function useCriarProduto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) =>
      post<{ data: ProdutoCatalogoDetalhe }>('/products', input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogoKeys.todos }),
  });
}

export function useAtualizarProduto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateProductInput }) =>
      patch<{ data: ProdutoCatalogoDetalhe }>(`/products/${id}`, input).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(catalogoKeys.produto(data.id), data);
      qc.invalidateQueries({ queryKey: ['catalogo', 'lista'] });
    },
  });
}

export function useExcluirProduto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => del<null>(`/products/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogoKeys.todos }),
  });
}

export function useReordenarProdutos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      post<{ data: ProdutoCatalogo[] }>('/products/reorder', { ids }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogoKeys.todos }),
  });
}

export function useSalvarAcessorios() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, items }: { productId: number; items: string[] }) =>
      put<{ data: Accessory[] }>(`/products/${productId}/accessories`, { items }).then((r) => r.data),
    onSuccess: (_data, { productId }) => {
      qc.invalidateQueries({ queryKey: catalogoKeys.produto(productId) });
      qc.invalidateQueries({ queryKey: ['catalogo', 'lista'] });
    },
  });
}

export function useCriarVersaoPreco() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, input }: { productId: number; input: CreateProductPriceInput }) =>
      post<{ data: PriceVersion }>(`/products/${productId}/prices`, input).then((r) => r.data),
    onSuccess: (_data, { productId }) => {
      qc.invalidateQueries({ queryKey: catalogoKeys.produto(productId) });
      qc.invalidateQueries({ queryKey: ['catalogo', 'lista'] });
    },
  });
}

export function useAtualizarVersaoPreco() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      versionId,
      input,
    }: {
      productId: number;
      versionId: number;
      input: UpdatePriceVersionInput;
    }) => patch<{ data: PriceVersion }>(`/products/${productId}/prices/${versionId}`, input).then((r) => r.data),
    onSuccess: (_data, { productId }) => {
      qc.invalidateQueries({ queryKey: catalogoKeys.produto(productId) });
      qc.invalidateQueries({ queryKey: ['catalogo', 'lista'] });
    },
  });
}

export function useExcluirVersaoPreco() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, versionId }: { productId: number; versionId: number }) =>
      del<null>(`/products/${productId}/prices/${versionId}`),
    onSuccess: (_data, { productId }) => {
      qc.invalidateQueries({ queryKey: catalogoKeys.produto(productId) });
      qc.invalidateQueries({ queryKey: ['catalogo', 'lista'] });
    },
  });
}
